// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

const ProgressBar = ({ percent = 0, color = '', height = 8 }: { percent?: number; color?: string; height?: number }) => (
  <div style={{ width: '100%', height, backgroundColor: T.bg4, borderRadius: height / 2, overflow: 'hidden' }}>
    <div style={{ width: `${Math.min(percent, 100)}%`, height: '100%', backgroundColor: color, borderRadius: height / 2, transition: 'width 0.5s ease' }} />
  </div>
);
const utilColor = (pct: number, t = [60, 80]) => pct >= t[1] ? T.red : pct >= t[0] ? T.ylw : T.grn;

// One System Health tile's worth of "this check did not produce a number".
//
// A PASS-THROUGH when the check is ok, which is the point: on a deployment
// where every probe works — every VPS — this renders its children and nothing
// else, so the screen is byte-for-byte what it was before. It only takes over
// when the backend says a check is `unavailable` or `error`.
//
// `unavailable` is styled NEUTRALLY, not as a failure. A container host has no
// host disk to report and that is a fact about the hosting, not a fault: it
// reads "Not applicable on this hosting" in muted text, with no red and no
// warning icon. Showing it as an error would train the reader to ignore the
// errors that matter.
const CheckGate = ({ st, children }: { st?: { status?: string; reason?: string }; children: any }) => {
  if (!st || !st.status || st.status === 'ok') return <>{children}</>;
  const neutral = st.status === 'unavailable';
  return (
    <div style={{ fontSize: 11, color: neutral ? T.txM : T.ylw, fontStyle: neutral ? 'italic' : 'normal', paddingTop: 4, lineHeight: 1.4 }}>
      {neutral ? (st.reason || 'Not applicable on this hosting') : `Could not be read: ${st.reason || 'check failed'}`}
    </div>
  );
};

export const PlatformHealthTab = () => {
  const [health, setHealth] = useState<any>(null);
  const [aiHealth, setAiHealth] = useState<any>(null);
  const [inboundHealth, setInboundHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<any>(null);
  const [serverStats, setServerStats] = useState<any>(null);
  const [statsAuto, setStatsAuto] = useState(true);
  const [statsTime, setStatsTime] = useState<any>(null);
  const [now, setNow] = useState(Date.now());
  // Consecutive server-stats failures. Drives the poll interval below: a feed
  // that is failing must not be re-asked every ten seconds. On the Certara
  // estate the 10s poll against a 500 produced 144 console errors while the
  // screen sat open, which is noise that buries whatever the real problem is.
  const [statsFailures, setStatsFailures] = useState(0);
  const [healthFailed, setHealthFailed] = useState(false);

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const loadHealth = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const [h, a, ih] = await Promise.all([
        apiCall("/settings/platform-health"),
        apiCall("/settings/ai-health"),
        apiCall("/settings/inbound-email-health"),
      ]);
      // apiCall resolves to null on a non-2xx and never throws (apiClient.ts:34),
      // so the catch below cannot fire on a 500 — the old code relied on it and
      // silently left `health` null, which is what blanked the whole screen.
      if (h) setHealth(h);
      if (a) setAiHealth(a);
      if (ih) setInboundHealth(ih);
      setHealthFailed(!h);
      setLastRefresh(new Date());
    } catch (e: any) { setHealthFailed(true); }
    setLoading(false);
  }, []);

  const loadStats = useCallback(async () => {
    const d = await apiCall("/settings/server-stats");
    if (d) {
      setServerStats(d);
      setStatsTime(new Date());
      setStatsFailures(0);
    } else {
      // Keep the last good reading on screen rather than throwing the panel
      // away; count the failure so the interval backs off.
      setStatsFailures((n) => n + 1);
    }
  }, []);

  useEffect(() => { loadHealth(true); loadStats(); }, [loadHealth, loadStats]);
  useEffect(() => { const i = setInterval(() => loadHealth(false), 30000); return () => clearInterval(i); }, [loadHealth]);
  // 10s while healthy, backing off to 30s then 2 minutes after repeated
  // failures. Unchanged on a working deployment, which is every VPS.
  const statsInterval = statsFailures === 0 ? 10000 : statsFailures < 3 ? 30000 : 120000;
  useEffect(() => { if (!statsAuto) return; const i = setInterval(loadStats, statsInterval); return () => clearInterval(i); }, [statsAuto, loadStats, statsInterval]);

  if (loading) return <div style={{ padding: 20, textAlign: "center", color: T.txM }}>Loading platform health...</div>;
  // DELIBERATELY NOT a bare early return any more. It used to be
  //   if (!health) return <div>Failed to load platform health data</div>
  // which is exactly what the owner saw on Certara: one endpoint answered 500
  // and the entire screen became that one red line, discarding the AI, inbound
  // email and server panels whose endpoints had all answered 200. A section
  // that cannot load is now one notice among the sections that can.

  const highConf = (aiHealth?.byConfidence || []).find((c: any) => c.confidence === "high")?.count || 0;
  const medConf = (aiHealth?.byConfidence || []).find((c: any) => c.confidence === "medium")?.count || 0;
  const lowConf = (aiHealth?.byConfidence || []).find((c: any) => c.confidence === "low")?.count || 0;
  const totalAiFields = aiHealth?.totalFields || 0;
  const avgConfScore = totalAiFields > 0 ? Math.round(((highConf * 95 + medConf * 75 + lowConf * 45) / totalAiFields)) : 0;

  return (
    <div>
      <SectionTitle sub={lastRefresh ? `Last updated: ${lastRefresh.toLocaleTimeString()}` : "System health and usage metrics"} right={<RefreshBtn onClick={() => { loadHealth(false); loadStats(); }} />}>Platform Health</SectionTitle>

      {/* Server Performance */}
      {serverStats && (() => {
        const s = serverStats;
        const ago = statsTime ? `${Math.round((+now - statsTime.getTime()) / 1000)}s ago` : '';
        return (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>Server Performance</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.txM, cursor: 'pointer' }}>
                {statsAuto && <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: T.grn, display: 'inline-block', animation: 'pulse 2s infinite' }} />}
                <input type="checkbox" checked={statsAuto} onChange={e => setStatsAuto(e.target.checked)} style={{ display: 'none' }} />
                Auto-refresh: {statsAuto ? 'ON' : 'OFF'}
              </label>
              <RefreshBtn onClick={() => { loadStats(); }} label="Refresh Stats" />
              {ago && <span style={{ fontSize: 10, color: T.txD }}>{ago}</span>}
            </div>
          </div>
          {/* Row 1: CPU, Memory, Swap, Disk */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', marginBottom: 6 }}>CPU Usage</div>
              <CheckGate st={s.checks?.cpu}>
              <div style={{ fontSize: 24, fontWeight: 800, color: utilColor(s.cpu.usage_percent), marginBottom: 6 }}>{s.cpu.usage_percent}%</div>
              <ProgressBar percent={s.cpu.usage_percent} color={utilColor(s.cpu.usage_percent)} />
              <div style={{ fontSize: 10, color: T.txM, marginTop: 6 }}>{s.cpu.cores} cores | Load: {s.cpu.load_1m}, {s.cpu.load_5m}, {s.cpu.load_15m}</div>
              </CheckGate>
            </Card>
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', marginBottom: 6 }}>Memory</div>
              <CheckGate st={s.checks?.memory}>
              <div style={{ fontSize: 24, fontWeight: 800, color: utilColor(s.memory.usage_percent), marginBottom: 6 }}>{s.memory.usage_percent}%</div>
              <ProgressBar percent={s.memory.usage_percent} color={utilColor(s.memory.usage_percent)} />
              <div style={{ fontSize: 10, color: T.txM, marginTop: 6 }}>{(s.memory.used_mb / 1024).toFixed(1)} / {(s.memory.total_mb / 1024).toFixed(1)} GB</div>
              <div style={{ fontSize: 10, color: T.txD }}>{(s.memory.available_mb / 1024).toFixed(1)} GB avail | {(s.memory.cached_mb / 1024).toFixed(1)} GB cached</div>
              </CheckGate>
            </Card>
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', marginBottom: 6 }}>Swap</div>
              <CheckGate st={s.checks?.swap}>
              <div style={{ fontSize: 24, fontWeight: 800, color: utilColor(s.swap.usage_percent, [25, 50]), marginBottom: 6 }}>{s.swap.usage_percent}%</div>
              <ProgressBar percent={s.swap.usage_percent} color={utilColor(s.swap.usage_percent, [25, 50])} />
              <div style={{ fontSize: 10, color: T.txM, marginTop: 6 }}>{(s.swap.used_mb / 1024).toFixed(1)} / {(s.swap.total_mb / 1024).toFixed(1)} GB</div>
              </CheckGate>
            </Card>
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', marginBottom: 6 }}>Disk</div>
              <CheckGate st={s.checks?.disk}>
              <div style={{ fontSize: 24, fontWeight: 800, color: utilColor(parseInt(s.disk.percent) || 0, [70, 85]), marginBottom: 6 }}>{s.disk.percent}</div>
              <ProgressBar percent={parseInt(s.disk.percent) || 0} color={utilColor(parseInt(s.disk.percent) || 0, [70, 85])} />
              <div style={{ fontSize: 10, color: T.txM, marginTop: 6 }}>{s.disk.used} / {s.disk.total} used</div>
              <div style={{ fontSize: 10, color: T.txD }}>{s.disk.available} available</div>
              </CheckGate>
            </Card>
          </div>
          {/* Row 2: Uptime, Network, PostgreSQL */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', marginBottom: 6 }}>Uptime</div>
              <CheckGate st={s.checks?.uptime}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.acc }}>{s.uptime.formatted}</div>
              </CheckGate>
              {/* The app's OWN uptime, deliberately outside the gate: it is the
                  one reading that needs no host facility, so it is meaningful on
                  every hosting even when the host uptime above is not. */}
              <div style={{ fontSize: 10, color: T.txM, marginTop: 6 }}>Node.js: {s.process.uptime_formatted} | PID {s.process.pid}</div>
              <div style={{ fontSize: 10, color: T.txD }}>{s.process.node_version}</div>
            </Card>
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', marginBottom: 6 }}>Network I/O</div>
              {(s.network || []).map((n: any) => (
                <div key={n.interface} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.tx, padding: '2px 0' }}>
                  <span style={{ fontWeight: 600 }}>{n.interface}</span>
                  <span style={{ color: T.txM }}>RX {n.rx_human} | TX {n.tx_human}</span>
                </div>
              ))}
              {(!s.network || s.network.length === 0) && (
                <CheckGate st={s.checks?.network}><div style={{ fontSize: 11, color: T.txD }}>No interfaces</div></CheckGate>
              )}
            </Card>
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', marginBottom: 6 }}>PostgreSQL</div>
              <CheckGate st={s.checks?.postgres}>
              <div style={{ fontSize: 11, color: T.tx, marginBottom: 4 }}>Size: <strong>{s.postgres.size}</strong></div>
              <div style={{ fontSize: 11, color: T.tx, marginBottom: 4 }}>Connections: <strong>{s.postgres.connections}</strong></div>
              {(s.postgres.byState || []).map((st: any) => (
                <div key={st.state || 'null'} style={{ fontSize: 10, color: T.txM }}>{st.state || 'idle'}: {st.count}</div>
              ))}
              </CheckGate>
            </Card>
          </div>
          {/* Row 3: Node.js Process */}
          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', marginBottom: 8 }}>Node.js Process</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[
                { l: 'RSS Memory', v: `${s.process.rss_mb} MB` },
                { l: 'Heap Used', v: `${s.process.heap_used_mb} / ${s.process.heap_total_mb} MB` },
                { l: 'External', v: `${s.process.external_mb} MB` },
                { l: 'Process Uptime', v: s.process.uptime_formatted },
              ].map(m => (
                <div key={m.l}><div style={{ fontSize: 10, color: T.txD }}>{m.l}</div><div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>{m.v}</div></div>
              ))}
            </div>
          </Card>
        </div>
        );
      })()}

      {/* Usage and data sections. These come from /settings/platform-health; the
          server panels above come from /settings/server-stats. They are rendered
          independently on purpose — before 2026-10-07 a failure of either made
          the whole screen a single red line. */}
      {!health && (
        <Card style={{ padding: 14, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Usage and data</div>
          <div style={{ fontSize: 12, color: T.txM }}>
            These figures could not be loaded. The rest of this screen is unaffected.
          </div>
        </Card>
      )}
      {/* FILE STORAGE. Shown only when the answer is actually "temporary", so a
          durable installation (the VPS, and Azure dev with its Azure Files
          share) sees nothing at all, and an installation where the probe could
          not tell also sees nothing — an honest unknown must not be dressed as
          either reassurance or alarm. The sentence is the server's own. */}
      {health?.storage?.persistent === false && (
        <Card style={{ padding: 14, marginBottom: 20, backgroundColor: "rgba(208,164,64,.08)", border: "1px solid rgba(208,164,64,.3)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 4 }}>File storage is temporary</div>
          <div style={{ fontSize: 12, color: T.tx, lineHeight: 1.5, marginBottom: 6 }}>
            {health.storage.warning}
          </div>
          <div style={{ fontSize: 12, color: T.txM, lineHeight: 1.5 }}>
            Every document anyone uploads here is erased when this application restarts, while the record pointing at it stays
            behind. Attach permanent storage at <strong>{health.storage.directory}</strong>. Until then, treat anything uploaded
            as temporary and keep a copy elsewhere.
          </div>
          <div style={{ fontSize: 10, color: T.txD, marginTop: 8 }}>Evidence: {health.storage.evidence}</div>
        </Card>
      )}

      {/* ROLE AND PERMISSION INTEGRITY (2026-10-19). Shown only when something
          is actually wrong, in the same spirit as the storage panel above. Two
          findings the server audits: a built-in role missing a permission the
          product ships it with, and a user whose displayed role and authorizing
          role disagree (the promoted-admin case: the screen says System Admin,
          the permission checks still run as the old role). */}
      {health?.rbac?.ok === false && (
        <Card style={{ padding: 14, marginBottom: 20, backgroundColor: "rgba(208,164,64,.08)", border: "1px solid rgba(208,164,64,.3)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Role permissions need attention</div>
          {Array.isArray(health.rbac.desynced_users) && health.rbac.desynced_users.length > 0 && (
            <div style={{ fontSize: 12, color: T.tx, lineHeight: 1.5, marginBottom: 6 }}>
              {health.rbac.desynced_users.length} user{health.rbac.desynced_users.length > 1 ? "s" : ""} display one role but are authorized as another. Re-saving the user's role on the Users screen corrects this; the next upgrade also corrects it automatically.
              <div style={{ marginTop: 4, color: T.txM }}>{health.rbac.desynced_users.slice(0, 5).map((u: any) => `${u.email} shows ${u.role}, authorizes as ${u.authorizing_role}`).join("; ")}</div>
            </div>
          )}
          {Array.isArray(health.rbac.missing_system_grants) && health.rbac.missing_system_grants.length > 0 && (
            <div style={{ fontSize: 12, color: T.tx, lineHeight: 1.5 }}>
              {health.rbac.missing_system_grants.length} built-in role permission{health.rbac.missing_system_grants.length > 1 ? "s differ" : " differs"} from the shipped defaults.
              <div style={{ marginTop: 4, color: T.txM }}>{health.rbac.missing_system_grants.slice(0, 8).map((m: any) => `${m.slug} is missing ${m.permission}`).join("; ")}</div>
            </div>
          )}
        </Card>
      )}

      {health && (<>
      {/* System metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total Users", val: health.users?.total || 0, c: T.acc },
          { label: "Logins (7d)", val: health.logins?.last7d || 0, c: T.blu },
          { label: "Logins (30d)", val: health.logins?.last30d || 0, c: T.prp },
          { label: "Database Size", val: health.database?.size || "N/A", c: T.cyn },
        ].map(m => (
          <Card key={m.label} style={{ padding: 14, textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: m.c }}>{m.val}</div>
            <div style={{ fontSize: 10, color: T.txD, marginTop: 4 }}>{m.label}</div>
          </Card>
        ))}
      </div>
      </>)}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Fed by /settings/platform-health. Gated so that endpoint failing
            costs these four cards and not the Inbound Email card below, which
            is a sibling in this same grid but comes from its own endpoint. */}
        {health && (<>
        {/* Users by role */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Users by Role</div>
          {(health.users?.byRole || []).map((r: any) => (
            <div key={r.role} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
              <span style={{ fontSize: 12, color: T.txM, textTransform: "capitalize" }}>{r.role}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.tx }}>{r.count}</span>
            </div>
          ))}
        </Card>

        {/* Data counts */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Data Volume</div>
          {[
            { label: "Contract Requests", val: health.data?.requests || 0 },
            { label: "Executed Contracts", val: health.data?.contracts || 0 },
            { label: "AI Extracted Fields", val: health.data?.aiFields || 0 },
            { label: "Activity Log Entries", val: health.data?.activityLogs || 0 },
            { label: "Unread Notifications", val: health.notifications?.unread || 0 },
          ].map(d => (
            <div key={d.label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
              <span style={{ fontSize: 12, color: T.txM }}>{d.label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.tx }}>{d.val.toLocaleString()}</span>
            </div>
          ))}
        </Card>

        {/* Import health */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Import Health (30 days)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div style={{ padding: 12, backgroundColor: T.bg3, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.acc }}>{health.imports?.total || 0}</div>
              <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>Total Imports</div>
            </div>
            <div style={{ padding: 12, backgroundColor: T.bg3, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.grn }}>{health.imports?.success || 0}</div>
              <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>Successful</div>
            </div>
            <div style={{ padding: 12, backgroundColor: T.bg3, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: (health.imports?.total || 0) - (health.imports?.success || 0) > 0 ? T.red : T.grn }}>{(health.imports?.total || 0) - (health.imports?.success || 0)}</div>
              <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>Failed</div>
            </div>
          </div>
        </Card>

        {/* SharePoint + integrations */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Integrations</div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
            <span style={{ fontSize: 12, color: T.txM }}>SharePoint Sync</span>
            <Badge c={health.sharepoint?.enabled ? T.grn : T.txD} b={health.sharepoint?.enabled ? T.grnS : T.bg4}>{health.sharepoint?.enabled ? "Enabled" : "Disabled"}</Badge>
          </div>
        </Card>
        </>)}

        {/* Inbound Email (reviewer reply) pipeline */}
        {inboundHealth && (() => {
          const ih = inboundHealth;
          const minsSince = (iso: any) => {
            if (!iso) return null;
            const ms = Date.now() - new Date(iso).getTime();
            if (isNaN(ms)) return null;
            return Math.round(ms / 60000);
          };
          const fmtSince = (mins: number | null) => {
            if (mins === null) return "never";
            if (mins < 1) return "just now";
            if (mins < 60) return `${mins}m ago`;
            if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
            return `${Math.round(mins / 1440)}d ago`;
          };
          const webhookMins = minsSince(ih.last_webhook_at);
          const pollMins = minsSince(ih.last_poll_processed_at);
          // Thresholds: fresh < 2h, amber 2-12h, red > 12h or never
          const dot = (mins: number | null, strictRed = 720) => {
            if (mins === null) return T.txD;
            if (mins < 120) return T.grn;
            if (mins < strictRed) return T.ylw;
            return T.red;
          };
          const subExpMins = ih.subscription?.expiration_date_time
            ? Math.round((new Date(ih.subscription.expiration_date_time).getTime() - Date.now()) / 60000)
            : null;
          const subColor = subExpMins === null ? T.txD : subExpMins < 60 ? T.red : subExpMins < 720 ? T.ylw : T.grn;
          const invites = ih.recent_reviewer_invites_24h || 0;
          const feedback = ih.recent_reviewer_feedback_24h || 0;
          const invitesNoFeedback = invites > 0 && feedback === 0 && (webhookMins === null || webhookMins > 120);
          const Dot = ({ c }: { c: string }) => <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: c, marginRight: 6 }} />;
          return (
            <Card style={{ gridColumn: "1 / -1" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Inbound Email (Reviewer Reply Pipeline)</div>
                {invitesNoFeedback && <Badge c={T.red} b={T.redS || T.bg4}>No replies ingested</Badge>}
              </div>
              {ih.subscription_error && (
                <div style={{ padding: 8, backgroundColor: T.bg4, borderRadius: 4, fontSize: 11, color: T.red, marginBottom: 10 }}>
                  Graph subscription probe failed: {ih.subscription_error}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                <div style={{ padding: 12, backgroundColor: T.bg3, borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", marginBottom: 4 }}>Last Webhook</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.tx }}><Dot c={dot(webhookMins)} />{fmtSince(webhookMins)}</div>
                  <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>{ih.last_webhook_at ? new Date(ih.last_webhook_at).toLocaleString() : "—"}</div>
                </div>
                <div style={{ padding: 12, backgroundColor: T.bg3, borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", marginBottom: 4 }}>Last Poll Processed</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.tx }}><Dot c={dot(pollMins)} />{fmtSince(pollMins)}</div>
                  <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>{ih.last_poll_processed_at ? new Date(ih.last_poll_processed_at).toLocaleString() : "—"}</div>
                </div>
                <div style={{ padding: 12, backgroundColor: T.bg3, borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", marginBottom: 4 }}>Graph Subscription</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>
                    <Dot c={subColor} />
                    {subExpMins === null ? "none" : subExpMins < 0 ? "expired" : subExpMins < 60 ? `expires ${subExpMins}m` : subExpMins < 1440 ? `expires ${Math.round(subExpMins / 60)}h` : `expires ${Math.round(subExpMins / 1440)}d`}
                  </div>
                  <div style={{ fontSize: 10, color: T.txD, marginTop: 2, wordBreak: "break-all" }}>{ih.subscription?.id ? ih.subscription.id.slice(0, 8) + "…" : "—"}</div>
                </div>
                <div style={{ padding: 12, backgroundColor: T.bg3, borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", marginBottom: 4 }}>Reviewer Activity (24h)</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>{invites} invites → {feedback} replies</div>
                  <div style={{ fontSize: 10, color: invitesNoFeedback ? T.red : T.txD, marginTop: 2 }}>
                    {invitesNoFeedback ? "Invites sent but no replies ingested" : "Delta expected < 24h lag"}
                  </div>
                </div>
              </div>
              {ih.subscription?.mailbox_unread && Object.keys(ih.subscription.mailbox_unread).length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 6 }}>Unread in monitored mailboxes</div>
                  {Object.entries(ih.subscription.mailbox_unread).map(([mb, count]: any) => (
                    <div key={mb} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
                      <span style={{ fontSize: 11, color: T.txM }}>{mb}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: count === null ? T.txD : count > 0 ? T.ylw : T.tx }}>{count === null ? "probe failed" : count}</span>
                    </div>
                  ))}
                </div>
              )}
              {ih.last_subscription_recreated_at && (
                <div style={{ marginTop: 10, fontSize: 10, color: T.txD }}>
                  Last zombie-triggered recreate: {new Date(ih.last_subscription_recreated_at).toLocaleString()}
                </div>
              )}
            </Card>
          );
        })()}

        {/* AI Extraction health */}
        <Card style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>AI Extraction Health</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
            {[
              { label: "Total Fields", val: totalAiFields, c: T.acc },
              { label: "High Confidence", val: highConf, c: T.grn },
              { label: "Medium Confidence", val: medConf, c: T.ylw },
              { label: "Low Confidence", val: lowConf, c: lowConf > 0 ? T.red : T.grn },
              { label: "Avg Confidence", val: `${avgConfScore}%`, c: avgConfScore >= 80 ? T.grn : avgConfScore >= 60 ? T.ylw : T.red },
            ].map(a => (
              <div key={a.label} style={{ padding: 12, backgroundColor: T.bg3, borderRadius: 6, textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: a.c }}>{typeof a.val === "number" ? a.val.toLocaleString() : a.val}</div>
                <div style={{ fontSize: 9, color: T.txD, marginTop: 2 }}>{a.label}</div>
              </div>
            ))}
          </div>
          {aiHealth?.byContractType?.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 8 }}>Extractions by Contract Type</div>
              {aiHealth.byContractType.map((t: any) => (
                <div key={t.contract_type} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
                  <span style={{ fontSize: 11, color: T.txM }}>{t.contract_type || "Unknown"}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.tx }}>{t.count}</span>
                </div>
              ))}
            </div>
          )}
          {aiHealth?.topFields?.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 8 }}>Most Extracted Fields</div>
              {aiHealth.topFields.slice(0, 8).map((f: any) => (
                <div key={f.field_name} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
                  <span style={{ fontSize: 11, color: T.txM }}>{f.field_name}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.tx }}>{f.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

/* ═══════════════════ ADMIN ═══════════════════ */

